import axios from "axios";
import { useState, useEffect, useContext, useCallback } from "react";
import { AuthContext } from "../../context/AuthContext";
import {
    Table,
    Card,
    Input,
    Row,
    Col,
    Select,
    Modal,
    Form,
    InputNumber,
    Button,
    DatePicker,
    Upload,
    message,
    Typography,
} from "antd";
import {
    PlusOutlined,
    UploadOutlined,
    DownloadOutlined,
} from "@ant-design/icons";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as XLSX from "xlsx";
import { toWords } from "number-to-words";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import SalaryCalculator from "../../components/SalaryCalculator";
import SalaryCalculation from "./SalaryCalculation";

const { Title } = Typography;

const HR_TAB_STATUSES = {
    Draft: ["Draft"],
    Pending: ["Pending"],
    Approved: ["Approved"],
    Rejected: ["Rejected"],
};

/* ------------------- CSRF Fix ------------------- */
const getCsrfToken = () => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; csrftoken=`);
    if (parts.length === 2) return parts.pop().split(";").shift();
    return "";
};

axios.interceptors.request.use((config) => {
    const isExternal =
        config.url &&
        (config.url.startsWith("http://") || config.url.startsWith("https://")) &&
        !config.url.startsWith(window.location.origin) &&
        !config.url.includes("127.0.0.1") &&
        !config.url.includes("localhost") &&
        !config.url.includes("192.168.1.160");

    if (isExternal) {
        if (config.headers) {
            delete config.headers["X-CSRFToken"];
            delete config.headers["x-csrftoken"];
            delete config.headers["X-Csrftoken"];
        }
    } else {
        const csrfToken = getCsrfToken();
        if (csrfToken) config.headers["X-CSRFToken"] = csrfToken;
        config.withCredentials = true;
    }
    return config;
});

/* ------------------- Helpers ------------------- */
const numberToWords = (num) => {
    if (!num || num === 0) return "Zero";
    return (
        toWords(Math.round(num)).replace(/\b\w/g, (l) => l.toUpperCase()) + " Only"
    );
};

/* ------------------- calculateSalary ------------------- */
// CHANGES:
//   • other_allowance_earned  →  special_allowance_earned  (RENAMED)
//   • gross_salary added as a separate input field         (NEW)
//   • flexible_benefit_allowance added                     (NEW)
//   • tds added to deductions                              (NEW)
const calculateSalary = (values) => {
    const {
        basic_earned = 0,
        hra_earned = 0,
        conveyance_earned = 0,
        special_allowance_earned = 0,   // RENAMED from other_allowance_earned
        flexible_benefit_allowance = 0, // NEW
        pf_employee = 0,
        esi_employee = 0,
        pt_pm = 0,
        tds = 0,   // NEW
    } = values;

    const gross_earned =
        Number(basic_earned) +
        Number(hra_earned) +
        Number(conveyance_earned) +
        Number(special_allowance_earned) +
        Number(flexible_benefit_allowance); // NEW: included in gross_earned calc

    const total_deductions =
        Number(pf_employee) +
        Number(esi_employee) +
        Number(pt_pm) +
        Number(tds); // NEW

    const net_pay = gross_earned - total_deductions;
    const net_pay_in_words = numberToWords(net_pay);

    // ESI eligibility rule: No if gross >= 23447, else Yes
    const is_esi = gross_earned >= 23447 ? "No" : "Yes";

    return {
        gross_earned,
        total_deductions,
        net_pay,
        net_pay_in_words,
        is_esi,
    };
};

export default function ActualSalaryStatement() {
    /* ------------------- State ------------------- */
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [departmentNames, setDepartmentNames] = useState([]);
    const [designationNames, setDesignationNames] = useState([]);
    const [organizationNames, setOrganizationNames] = useState([]);
    const [form] = Form.useForm();
    const [dirtyFields, setDirtyFields] = useState(new Set());
    const [selectedSalary, setSelectedSalary] = useState({
        id: 0,
        basic: 0,
        hra: 0,
        conveyance: 0,
        special_allowance: 0,        // RENAMED from others
        flexible_benefit_allowance: 0, // NEW
        gross_salary: 0,             // NEW
        pf: 0,
        esi: 0,
        tds: 0,                      // NEW
        gross: 0,
        total_deduction: 0,
        net: 0,
    });

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    const { backUrl, authData } = useContext(AuthContext);
    const isSuperuser = authData?.employee?.is_superuser || authData?.is_superuser;

    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedKeys) => setSelectedRowKeys(selectedKeys),
        getCheckboxProps: () => ({}),
    };

    const [activeStatusTab, setActiveStatusTab] = useState("Draft");
    const [loading, setLoading] = useState(false);

    const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [rejecting, setRejecting] = useState(false);
    const [filters, setFilters] = useState({
        name: "",
        emp_code: "",
        month: "",
        year: "",
        organization: "",
        department: "",
        designation: "",
    });

    const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
    const [uploadForm] = Form.useForm();
    const [uploading, setUploading] = useState(false);

    const [isGenerateModalVisible, setIsGenerateModalVisible] = useState(false);
    const [generateForm] = Form.useForm();
    const [generating, setGenerating] = useState(false);

    /* ------------------- API ------------------- */
    const fetchData = async () => {
        try {
            setLoading(true);
            const headers = { Authorization: `Bearer ${authData?.access}` };
            const statuses = HR_TAB_STATUSES[activeStatusTab] || [activeStatusTab];
            const p = new URLSearchParams();
            p.append("salary_status", statuses.join(","));
            if (filters.month) p.append("month", filters.month);
            if (filters.year) p.append("year", filters.year);

            const res = await axios.get(
                `${backUrl}/api/payroll/drafts/?${p.toString()}`,
                { headers }
            );
            const results = res.data.results || [];
            setData(results);
            setFilteredData(results);
            setSelectedRowKeys([]);
        } catch (error) {
            console.error("Error fetching salary statement:", error);
            toast.error("Failed to fetch data");
        } finally {
            setLoading(false);
        }
    };

    const fetchOrganizations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`);
            setOrganizationNames(res.data.results);
        } catch (err) {
            console.error("Failed to fetch organizations:", err);
        }
    };

    const fetchDepartments = async (v) => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/departments/?organisation=${v}`);
            setDepartmentNames(res.data.results);
        } catch (err) {
            console.error("Failed to fetch departments:", err);
        }
    };

    const fetchDesignations = async (departmentId) => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/designations/?department=${departmentId}`);
            setDesignationNames(res.data.results);
        } catch (err) {
            console.error("Error fetching designations", err);
        }
    };

    /* ------------------- Generate Salary Modal ------------------- */
    const handleOpenGenerateModal = () => {
        generateForm.resetFields();
        setIsGenerateModalVisible(true);
    };
    const handleCloseGenerateModal = () => {
        setIsGenerateModalVisible(false);
        generateForm.resetFields();
    };

    const handleGenerateSalary = async () => {
        try {
            const values = await generateForm.validateFields();
            const { month_year } = values;
            const month = dayjs(month_year).month() + 1;
            const year = dayjs(month_year).year();

            setGenerating(true);
            message.loading({ content: "Generating salary...", key: "generateSalary" });

            const res = await axios.post(
                `${backUrl}/api/payroll/generate-all-drafts/?month=${month}&year=${year}`,
                {},
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            message.success({ content: res.data.message, key: "generateSalary" });
            toast.success(res.data.message);
            handleCloseGenerateModal();
            fetchData();
        } catch (error) {
            console.error("Error generating salary:", error);
            message.error({ content: "Failed to generate salary", key: "generateSalary" });
            toast.error(error.response?.data?.detail || "Failed to generate salary");
        } finally {
            setGenerating(false);
        }
    };

    /* ------------------- Upload ------------------- */
    const handleOpenUploadModal = () => setIsUploadModalVisible(true);
    const handleCloseUploadModal = () => {
        setIsUploadModalVisible(false);
        uploadForm.resetFields();
    };

    const handleUploadSalary = async () => {
        try {
            const values = await uploadForm.validateFields();
            const { month_year, file } = values;
            const formData = new FormData();
            const month = dayjs(month_year).month() + 1;
            const year = dayjs(month_year).year();

            formData.append("month", month.toString());
            formData.append("year", year.toString());
            formData.append("file", file[0].originFileObj);

            setUploading(true);
            const res = await axios.post(
                `${backUrl}/api/payroll/salary/bulk-upload/`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );
            toast.success(res.data.message || "Salary details uploaded successfully!");
            handleCloseUploadModal();
            fetchData();
        } catch (error) {
            console.error("Upload error:", error);
            toast.error(error.response?.data?.detail || "Failed to upload salary details!");
        } finally {
            setUploading(false);
        }
    };

    /* ------------------- Download Excel ------------------- */
    // CHANGES: other_allowance_earned → special_allowance_earned (RENAMED)
    //          gross_salary added (NEW)
    //          flexible_benefit_allowance already existed, kept
    //          tds added (NEW)
    const handleDownloadExcel = () => {
        if (!filteredData.length) {
            toast.warning("No records to download!");
            return;
        }

        const excelData = filteredData.map((item) => ({
            "Emp Name": item.emp_name || "",
            "Emp Code": item.employee?.emp_code || "",
            "Name": `${item.employee?.first_name || ""} ${item.employee?.last_name || ""}`,
            "Organisation": item.employee?.organisation_name || "",
            "Designation": item.designation || "",
            "Department": item.department || "",
            "DOJ": item.doj || "",
            "Bank Name": item.bank_name || "",
            "IFSC Code": item.ifsc_code || "",
            "Account No": item.account_no || "",
            "Bank Branch": item.bank_branch || "",
            "PF UAN": item.pf_uan || "",
            "ESCI No": item.esci_no || "",
            "PAN No": item.pan_no || "",
            "Aadhar No": item.aadhar_no || "",
            "Month Days": item.month_days || "",
            "Date": item.date || "",
            "Days Payable": item.days_payable || "",
            "LOP Days": item.lop_days || "",
            "Basic PM": item.basic_pm || "",
            "HRA PM": item.hra_pm || "",
            "Conveyance PM": item.conveyance_pm || "",
            "Special Allowance PM": item.special_allowance_pm || "",
            "OT Pay PM": item.ot_pay_pm || "",
            "Arrears PM": item.arrears_pm || "",
            "Actual Basic PM": item.actual_basic_pm || "",
            "Actual HRA PM": item.actual_hra_pm || "",
            "Actual Conveyance PM": item.actual_conveyance_pm || "",
            "Actual Special Allowance PM": item.actual_special_allowance_pm || "",
            "Actual OT Pay PM": item.actual_ot_pay_pm || "",
            "Actual Arrears PM": item.actual_arrears_pm || "",
            "Basic": item.basic_earned || "",
            "HRA": item.hra_earned || "",
            "Conveyance": item.conveyance_earned || "",
            "Special Allowance": item.special_allowance_earned || "",
            "Actual Other Allowance": item.actual_other_allowance || "",
            "fixed Other Allowance": item.fixed_other_allowance || "",
            "Flexible Benefit Allowance": item.flexible_benefit_allowance || "",
            "Gross Earned": item.gross_earned || "",
            "Gross Salary": item.gross_salary || "",
            "OT Days": item.no_of_ot_days || "",
            "OT Pay": item.ot_pay || "",
            "PT": item.pt_pm || "",
            "Employer PF (12%)": item.pf_employer || "",
            "Employer ESI (3.25%)": item.esi_employer || "",
            "Employee PF": item.pf_employee || "",
            "Employee ESI": item.esi_employee || "",
            "TDS": item.tds || "",
            "Late Days": item.late_days || "",
            "Late Deduction": item.late_deduction || "",
            "LOP Deduction": item.lop_deduction || "",
            "Salary Advance": item.salary_advance || "",
            "Other Deductions": item.other_deductions || "",
            "Arrears": item.arrears || "",
            "Total CTC": item.ctc_monthly || "",
            "Total Deductions": item.total_deductions || "",
            "Fixed Earning": item.fixed_earning || "",
            "Actual Earning": item.actual_earning || "",
            "Net Pay": item.net_pay || "",
            "Net Pay in Words": item.net_pay_in_words || "",
            "Salary Type": item.salary_type || "",
            "Salary Status": item.salary_status || "",
            "CL Total": item.cl_total ?? "",
            "CL Taken": item.cl_taken ?? "",
            "CL Balance": item.cl_balance ?? "",
            "SL Total": item.sl_total ?? "",
            "SL Taken": item.sl_taken ?? "",
            "SL Balance": item.sl_balance ?? "",
            "CL Total (Legacy)": item.leave_balance?.CL?.total ?? "",
            "CL Used (Legacy)": item.leave_balance?.CL?.used ?? "",
            "CL Balance (Legacy)": item.leave_balance?.CL?.balance ?? "",
            "SL Total (Legacy)": item.leave_balance?.SL?.total ?? "",
            "SL Used (Legacy)": item.leave_balance?.SL?.used ?? "",
            "SL Balance (Legacy)": item.leave_balance?.SL?.balance ?? "",
            "Date (Salary Sheet)": item.date || "",
            "Month": item.salary_month || "",
            "Year": item.year || "",
            "Approval Status": item.salary_status || "",
        }));

        const ws = XLSX.utils.json_to_sheet(excelData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Salary Statement");
        XLSX.writeFile(wb, "Actual_Salary_Statement.xlsx");
        toast.success("Excel downloaded successfully!");
    };

    /* ------------------- Filtering ------------------- */
    useEffect(() => { fetchData(); }, [activeStatusTab, filters.month, filters.year]);
    useEffect(() => { fetchOrganizations(); }, []);

    useEffect(() => {
        let filtered = [...data];

        if (filters.organization)
            filtered = filtered.filter((i) => i.employee?.organisation_name === filters.organization);
        if (filters.department)
            filtered = filtered.filter((i) => i.employee?.department_name === filters.department);
        if (filters.designation)
            filtered = filtered.filter((i) => i.employee?.designation_name === filters.designation);
        if (filters.emp_code)
            filtered = filtered.filter((i) =>
                i.employee?.emp_code?.toLowerCase().includes(filters.emp_code.toLowerCase())
            );
        if (filters.name)
            filtered = filtered.filter((i) => {
                const full = `${i.employee?.first_name || ""} ${i.employee?.last_name || ""}`.toLowerCase();
                return full.includes(filters.name.toLowerCase());
            });

        setFilteredData(filtered);
    }, [filters.organization, filters.department, filters.designation, filters.emp_code, filters.name, data]);

    /* ------------------- Editable Modal ------------------- */
    const handleOpenModal = (record) => {
        setSelectedRecord(record);
        form.setFieldsValue(record);
        setDirtyFields(new Set());
        setIsModalVisible(true);
    };

    const handleCloseModal = () => {
        setIsModalVisible(false);
        setSelectedRecord(null);
        form.resetFields();
        setDirtyFields(new Set());
    };

    const onValuesChange = (changed, all) => {
        const changedKey = Object.keys(changed)[0];
        setDirtyFields((prev) => new Set(prev).add(changedKey));
        const calc = calculateSalary(all);
        form.setFieldsValue(calc);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const payload = {};
            dirtyFields.forEach((field) => { payload[field] = values[field]; });
            const calc = calculateSalary(values);
            Object.assign(payload, calc);

            await axios.post(
                `${backUrl}/api/payroll/generate-salary/${selectedRecord.id}/?month=${selectedRecord.month}&year=${selectedRecord.year}`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                        "Content-Type": "application/json",
                    },
                }
            );
            toast.success("Salary details updated successfully!");
            handleCloseModal();
            fetchData();
        } catch (error) {
            console.error("Error updating salary:", error);
            toast.error(error.response?.data?.detail || "Failed to update salary details");
        }
    };

    /* ------------------- Filter Handlers ------------------- */
    const handleOrgChange = (orgId) => {
        const org = organizationNames.find((o) => o.id === orgId);
        setFilters({ ...filters, organization: org?.name || "", department: "", designation: "" });
        setDepartmentNames([]);
        setDesignationNames([]);
        if (orgId) fetchDepartments(orgId);
    };

    const handleDeptChange = (deptId) => {
        const dept = departmentNames.find((d) => d.id === deptId);
        setFilters({ ...filters, department: dept?.name || "", designation: "" });
        setDesignationNames([]);
        if (deptId) fetchDesignations(deptId);
    };

    const handleDesignationChange = (desId) => {
        const des = designationNames.find((d) => d.id === desId);
        setFilters({ ...filters, designation: des?.name || "" });
    };

    /* ------------------- Table Columns ------------------- */
    // CHANGES:
    //   • "Other Allowances" column → "Special Allowance" (dataIndex updated)   RENAMED
    //   • "Gross Salary" column added after "Gross Earned"                      NEW
    //   • "TDS" column added after Employee ESI                                 NEW
    //   • "Total Deductions" column added                                       NEW (was missing)
    const columns = [
        {
            title: "Emp Code",
            dataIndex: ["employee", "emp_code"],
            key: "emp_code",
            fixed: "left",
            render: (text, record) => (
                <a
                    style={{ color: "#1677ff", fontWeight: 500, cursor: "pointer" }}
                    onClick={() => {
                        const sheet = record.salary_sheet || {};
                        setSelectedSalary({
                            ...record,
                            id: record.id,
                            is_actual_structure: true,
                            basic: record.basic_earned ?? sheet.basic_pm,
                            hra: record.hra_earned ?? sheet.hra_pm,
                            conveyance: record.conveyance_earned ?? sheet.conveyance_pm,
                            special_allowance: record.special_allowance_earned ?? sheet.special_allowance_pm,
                            flexible_benefit_allowance: record.flexible_benefit_allowance ?? 0,
                            gross_salary: record.gross_salary ?? 0,
                            tds: record.tds ?? 0,
                            pf_employer: record.pf_employer ?? sheet.pf_employer_pm,
                            esi_employer: record.esi_employer ?? sheet.esi_employer_pm,
                            pf_employee: record.pf_employee ?? sheet.pf_employee_pm,
                            esi_employee: record.esi_employee ?? sheet.esi_employee_pm,
                            gross: record.gross_earned ?? sheet.gross_pm,
                            net: record.net_pay ?? sheet.net_pay_pm,
                        });
                        handleOpenModal(record);
                    }}
                >
                    {text}
                </a>
            ),
        },
        // NEW: Emp_name
        // { title: "Emp Name", dataIndex: "emp_name", key: "emp_name" },
        {
            title: "Name",
            key: "employee_name",
            render: (record) =>
                `${record.employee?.first_name || ""} ${record.employee?.last_name || ""}`,
        },
        {
            title: "Organisation",
            dataIndex: ["employee", "organisation_name"],
            key: "organisation_name",
            width: 200,
        },
        // NEW: Designation
        // { title: "Designation", dataIndex: "designation", key: "designation" },
        {
            title: "Designation",
            dataIndex: ["employee", "designation_name"],
            key: "designation_name",
            width: 200,
        },
        // NEW: Department
        // { title: "Department", dataIndex: "department", key: "department" },
        {
            title: "Department",
            dataIndex: ["employee", "department_name"],
            key: "department_name",
            width: 200,
        },
        // NEW: DOJ
        { title: "DOJ", dataIndex: "doj", key: "doj" },

        // NEW: Bank & Statutory Info
        { title: "Bank Name", dataIndex: "bank_name", key: "bank_name" },
        { title: "IFSC Code", dataIndex: "ifsc_code", key: "ifsc_code" },
        { title: "Account No", dataIndex: "account_no", key: "account_no" },
        { title: "Bank Branch", dataIndex: "bank_branch", key: "bank_branch" },
        { title: "PF UAN", dataIndex: "pf_uan", key: "pf_uan" },
        { title: "ESCI No", dataIndex: "esci_no", key: "esci_no" },
        { title: "PAN No", dataIndex: "pan_no", key: "pan_no" },
        { title: "Aadhar No", dataIndex: "aadhar_no", key: "aadhar_no" },

        { title: "Month Days", dataIndex: "month_days", key: "month_days" },
        // NEW: date
        { title: "Date", dataIndex: "date", key: "date_field" },
        { title: "Days Payable", dataIndex: "days_payable", key: "days_payable" },
        { title: "LOP Days", dataIndex: "lop_days", key: "lop_days_top" },

        // NEW: Fixed/Structured Salary Components
        { title: "Basic PM", dataIndex: "basic_pm", key: "basic_pm" },
        { title: "HRA PM", dataIndex: "hra_pm", key: "hra_pm" },
        { title: "Conveyance PM", dataIndex: "conveyance_pm", key: "conveyance_pm" },
        { title: "Actual Other Allowance", dataIndex: "actual_other_allowance", key: "actual_other_allowance" },
        { title: "Fixed Other Allowance", dataIndex: "fixed_other_allowance", key: "fixed_other_allowance" },
        { title: "Special Allowance PM", dataIndex: "special_allowance_pm", key: "special_allowance_pm" },
        { title: "OT Pay PM", dataIndex: "ot_pay_pm", key: "ot_pay_pm" },
        { title: "Arrears PM", dataIndex: "arrears_pm", key: "arrears_pm" },

        // NEW: Actual Earned (structured) Components
        { title: "Actual Basic PM", dataIndex: "actual_basic_pm", key: "actual_basic_pm" },
        { title: "Actual HRA PM", dataIndex: "actual_hra_pm", key: "actual_hra_pm" },
        { title: "Actual Conveyance PM", dataIndex: "actual_conveyance_pm", key: "actual_conveyance_pm" },
        { title: "Actual Special Allowance PM", dataIndex: "actual_special_allowance_pm", key: "actual_special_allowance_pm" },
        { title: "Actual OT Pay PM", dataIndex: "actual_ot_pay_pm", key: "actual_ot_pay_pm" },
        { title: "Actual Arrears PM", dataIndex: "actual_arrears_pm", key: "actual_arrears_pm" },

        { title: "Basic", dataIndex: "basic_earned", key: "basic_earned" },
        { title: "HRA", dataIndex: "hra_earned", key: "hra_earned" },
        { title: "Conveyance", dataIndex: "conveyance_earned", key: "conveyance_earned" },
        { title: "Special Allowance", dataIndex: "special_allowance_earned", key: "special_allowance_earned" },
        { title: "Flexible Benefit Allowance", dataIndex: "flexible_benefit_allowance", key: "flexible_benefit_allowance" },
        { title: "Gross Earned", dataIndex: "gross_earned", key: "gross_earned" },
        { title: "Gross Salary", dataIndex: "gross_salary", key: "gross_salary" },

        { title: "OT Days", dataIndex: "no_of_ot_days", key: "no_of_ot_days" },
        { title: "OT Pay", dataIndex: "ot_pay", key: "hourly_ot_amount" },
        { title: "PT", dataIndex: "pt_pm", key: "pt" },

        { title: "Employer PF (12%)", dataIndex: "pf_employer", key: "pf_employer" },
        { title: "Employer ESI (3.25%)", dataIndex: "esi_employer", key: "esi_employer" },
        { title: "Employee PF", dataIndex: "pf_employee", key: "pf_employee" },
        { title: "Employee ESI", dataIndex: "esi_employee", key: "esi_employee" },

        { title: "TDS", dataIndex: "tds", key: "tds" },

        { title: "Late Days", dataIndex: "late_days", key: "late_days" },
        { title: "Late Deduction", dataIndex: "late_deduction", key: "late_deduction" },
        { title: "LOP Deduction", dataIndex: "lop_deduction", key: "lop_deduction" },
        { title: "Salary Advance", dataIndex: "salary_advance", key: "salary_advance" },
        { title: "Other Deductions", dataIndex: "other_deductions", key: "other_deductions" },
        { title: "Arrears", dataIndex: "arrears", key: "arrears" },
        { title: "Total CTC", dataIndex: "ctc_monthly", key: "ctc_monthly" },
        { title: "Total Deductions", dataIndex: "total_deductions", key: "total_deductions" },

        // NEW: Fixed_earning, Actual_earning
        { title: "Fixed Earning", dataIndex: "fixed_earning", key: "fixed_earning" },
        { title: "Actual Earning", dataIndex: "actual_earning", key: "actual_earning" },

        { title: "Net Pay", dataIndex: "net_pay", key: "net_earned" },
        { title: "Net Pay in Words", dataIndex: "net_pay_in_words", key: "net_pay_in_words" },
        { title: "Salary Type", dataIndex: "salary_type", key: "salary_type" },
        { title: "Salary Status", dataIndex: "salary_status", key: "salary_status" },

        // NEW: flat CL/SL fields (in addition to existing nested leave_balance columns below)
        { title: "CL Total", dataIndex: "cl_total", key: "cl_total_flat" },
        { title: "CL Taken", dataIndex: "cl_taken", key: "cl_taken_flat" },
        { title: "CL Balance", dataIndex: "cl_balance", key: "cl_balance_flat" },
        { title: "SL Total", dataIndex: "sl_total", key: "sl_total_flat" },
        { title: "SL Taken", dataIndex: "sl_taken", key: "sl_taken_flat" },
        { title: "SL Balance", dataIndex: "sl_balance", key: "sl_balance_flat" },

        {
            title: "CL Total",
            key: "cl_total",
            render: (_, record) => record.leave_balance?.CL?.total ?? "-",
        },
        {
            title: "CL Used",
            key: "cl_used",
            render: (_, record) => record.leave_balance?.CL?.used ?? "-",
        },
        {
            title: "CL Balance",
            key: "cl_balance",
            render: (_, record) => record.leave_balance?.CL?.balance ?? "-",
        },
        {
            title: "SL Total",
            key: "sl_total",
            render: (_, record) => record.leave_balance?.SL?.total ?? "-",
        },
        {
            title: "SL Used",
            key: "sl_used",
            render: (_, record) => record.leave_balance?.SL?.used ?? "-",
        },
        {
            title: "SL Balance",
            key: "sl_balance",
            render: (_, record) => record.leave_balance?.SL?.balance ?? "-",
        },

        { title: "Date", dataIndex: "date", key: "date" },
        {
            title: "Month",
            dataIndex: "month",
            key: "month",
            render: (m) => {
                const num = parseInt(m, 10);
                return num >= 1 && num <= 12 ? dayjs().month(num - 1).format("MMMM") : "-";
            },
        },
        { title: "Year", dataIndex: "year", key: "year" },
        {
            title: "Approval Status",
            dataIndex: "salary_status",
            key: "approval_status",
            render: (text) => {
                const colorMap = {
                    Draft: "#8c8c8c",
                    Pending: "#faad14",
                    Approved: "#52c41a",
                    Rejected: "#ff4d4f",
                };
                return (
                    <span style={{ fontWeight: 600, color: colorMap[text] || "#000" }}>
                        {text || "-"}
                    </span>
                );
            },
        },
    ];

    /* ------------------- Bulk Actions ------------------- */
    const handleBulkApprove = async () => {
        if (!selectedRowKeys.length) { toast.warning("Please select at least one record!"); return; }
        try {
            const res = await axios.post(
                `${backUrl}/api/payroll/bulk_submit_salary/`,
                { ids: selectedRowKeys },
                { headers: { Authorization: `Bearer ${authData?.access}`, "Content-Type": "application/json" } }
            );
            toast.success(res.data.message || "Selected salaries submitted for approval!");
            setSelectedRowKeys([]);
            fetchData();
        } catch (error) {
            console.error("Bulk submit error:", error);
            toast.error(error.response?.data?.detail || "Failed to submit salaries");
        }
    };

    const handleSuperuserApprove = async () => {
        if (!selectedRowKeys.length) { toast.warning("Please select at least one record!"); return; }
        try {
            const res = await axios.post(
                `${backUrl}/api/payroll/bulk-approve-salary/`,
                { ids: selectedRowKeys },
                { headers: { Authorization: `Bearer ${authData?.access}`, "Content-Type": "application/json" } }
            );
            toast.success(res.data.message || `${selectedRowKeys.length} salaries approved!`);
            setSelectedRowKeys([]);
            fetchData();
        } catch (error) {
            console.error("Bulk approve error:", error);
            toast.error(error.response?.data?.detail || error.response?.data?.error || "Failed to approve salaries");
        }
    };

    const handleSuperuserReject = async () => {
        if (!rejectReason.trim()) { toast.warning("Please enter a rejection reason!"); return; }
        setRejecting(true);
        try {
            const res = await axios.post(
                `${backUrl}/api/payroll/bulk-reject-salary/`,
                { ids: selectedRowKeys, reason: rejectReason },
                { headers: { Authorization: `Bearer ${authData?.access}`, "Content-Type": "application/json" } }
            );
            toast.success(res.data.message || `${selectedRowKeys.length} salaries rejected!`);
            setSelectedRowKeys([]);
            setIsRejectModalVisible(false);
            setRejectReason("");
            fetchData();
        } catch (error) {
            console.error("Bulk reject error:", error);
            toast.error(error.response?.data?.detail || error.response?.data?.error || "Failed to reject salaries");
        } finally {
            setRejecting(false);
        }
    };

    /* ------------------- Render ------------------- */
    return (
        <>
            <ToastContainer position="top-right" autoClose={3000} />

            <center>
                <Title level={4} style={{ marginTop: 20, color: "#0e497b", textTransform: "uppercase" }}>
                    Monthly Salary Statement
                </Title>
            </center>

            {/* Status Tabs */}
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
                {[
                    ...(!isSuperuser ? [{ key: "Draft", label: "Draft", color: "#8c8c8c" }] : []),
                    { key: "Pending", label: "Pending", color: "#faad14" },
                    { key: "Approved", label: "Approved", color: "#52c41a" },
                    { key: "Rejected", label: "Rejected", color: "#ff4d4f" },
                ].map((tab) => (
                    <Button
                        key={tab.key}
                        type={activeStatusTab === tab.key ? "primary" : "default"}
                        onClick={() => { setActiveStatusTab(tab.key); setSelectedRowKeys([]); }}
                        style={{
                            marginRight: "10px",
                            marginBottom: "8px",
                            backgroundColor: activeStatusTab === tab.key ? tab.color : undefined,
                            borderColor: activeStatusTab === tab.key ? tab.color : undefined,
                            color: activeStatusTab === tab.key ? "#fff" : undefined,
                            fontWeight: "500",
                        }}
                    >
                        {tab.label}
                    </Button>
                ))}
            </div>

            {/* Filters */}
            <Card style={{ margin: 20 }}>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Select
                            placeholder="Organization"
                            style={{ width: "100%" }}
                            value={organizationNames.find((o) => o.name === filters.organization)?.id}
                            onChange={handleOrgChange}
                            allowClear
                        >
                            {organizationNames.map((org) => (
                                <Select.Option key={org.id} value={org.id}>{org.name}</Select.Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Select
                            placeholder="Department"
                            style={{ width: "100%" }}
                            value={departmentNames.find((d) => d.name === filters.department)?.id}
                            onChange={handleDeptChange}
                            allowClear
                            disabled={!filters.organization}
                        >
                            {departmentNames.map((dep) => (
                                <Select.Option key={dep.id} value={dep.id}>{dep.name}</Select.Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Select
                            placeholder="Designation"
                            style={{ width: "100%" }}
                            value={designationNames.find((d) => d.name === filters.designation)?.id}
                            onChange={handleDesignationChange}
                            allowClear
                            disabled={!filters.department}
                        >
                            {designationNames.map((des) => (
                                <Select.Option key={des.id} value={des.id}>{des.name}</Select.Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Input
                            placeholder="Emp Code"
                            value={filters.emp_code}
                            allowClear
                            onChange={(e) => setFilters({ ...filters, emp_code: e.target.value || "" })}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Input
                            placeholder="Name"
                            value={filters.name}
                            allowClear
                            onChange={(e) => setFilters({ ...filters, name: e.target.value || "" })}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={3}>
                        <Input
                            placeholder="Year"
                            type="number"
                            value={filters.year}
                            allowClear
                            onChange={(e) => setFilters({ ...filters, year: e.target.value || "" })}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={3}>
                        <Select
                            placeholder="Month"
                            style={{ width: "100%" }}
                            value={filters.month || undefined}
                            onChange={(v) => setFilters({ ...filters, month: v })}
                            allowClear
                        >
                            {Array.from({ length: 12 }, (_, i) => (
                                <Select.Option key={i + 1} value={i + 1}>
                                    {new Date(0, i).toLocaleString("default", { month: "long" })}
                                </Select.Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={3}>
                        <Button
                            style={{ width: "100%" }}
                            onClick={() =>
                                setFilters({ name: "", emp_code: "", month: "", year: "", organization: "", department: "", designation: "" })
                            }
                        >
                            Reset Filters
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* Action Buttons + Table */}
            <Card style={{ margin: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 15 }}>
                    <div style={{ display: "flex", gap: "16px" }}>
                        <Card size="small" style={{ borderRadius: "8px", background: "#f0f5ff", border: "1px solid #adc6ff" }}>
                            <span style={{ color: "#2f54eb", fontWeight: "bold" }}>Total Records: </span>
                            <span style={{ fontSize: 16, fontWeight: "bold" }}>{filteredData.length}</span>
                        </Card>
                        {selectedRowKeys.length > 0 && (
                            <Card size="small" style={{ borderRadius: "8px", background: "#f6ffed", border: "1px solid #b7eb8f" }}>
                                <span style={{ color: "#52c41a", fontWeight: "bold" }}>Selected: </span>
                                <span style={{ fontSize: 16, fontWeight: "bold" }}>{selectedRowKeys.length}</span>
                            </Card>
                        )}
                    </div>

                    <div style={{ display: "flex", gap: "10px" }}>
                        {activeStatusTab === "Draft" && !isSuperuser && selectedRowKeys.length > 0 && (
                            <Button
                                type="primary"
                                style={{ backgroundColor: "#1677ff", borderColor: "#1677ff", fontWeight: "500", color: "white" }}
                                onClick={handleBulkApprove}
                            >
                                Submit for Approval ({selectedRowKeys.length})
                            </Button>
                        )}

                        {activeStatusTab === "Pending" && isSuperuser && selectedRowKeys.length > 0 && (
                            <>
                                <Button
                                    type="primary"
                                    style={{ backgroundColor: "#52c41a", borderColor: "#52c41a", fontWeight: "500", color: "white" }}
                                    onClick={handleSuperuserApprove}
                                >
                                    Approve ({selectedRowKeys.length})
                                </Button>
                                <Button
                                    danger type="primary"
                                    style={{ fontWeight: "500" }}
                                    onClick={() => { setRejectReason(""); setIsRejectModalVisible(true); }}
                                >
                                    Reject ({selectedRowKeys.length})
                                </Button>
                            </>
                        )}

                        <Button
                            icon={<DownloadOutlined />}
                            style={{ backgroundColor: "#0e497b", borderColor: "#0e497b", color: "white", fontWeight: "500" }}
                            onClick={handleDownloadExcel}
                        >
                            Download Excel
                        </Button>

                        {activeStatusTab === "Draft" && !isSuperuser && (
                            <>
                                <Button
                                    icon={<UploadOutlined />}
                                    style={{ backgroundColor: "#0e497b", borderColor: "#0e497b", color: "white", fontWeight: "500" }}
                                    onClick={handleOpenUploadModal}
                                >
                                    Upload Salary Details
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    style={{ backgroundColor: "#0e497b", borderColor: "#0e497b", fontWeight: "500" }}
                                    onClick={handleOpenGenerateModal}
                                >
                                    Generate Salary
                                </Button>
                            </>
                        )}
                    </div>
                </div>

                <Table
                    rowSelection={rowSelection}
                    columns={columns}
                    dataSource={filteredData}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                    bordered
                    loading={loading}
                    scroll={{ x: 7700 }}
                    components={{
                        header: {
                            cell: (props) => (
                                <th
                                    {...props}
                                    style={{
                                        backgroundColor: "#0e497b",
                                        color: "#fff",
                                        textAlign: "center",
                                        padding: "8px 16px",     // default is around 16px
                                        height: "42px",
                                        lineHeight: "22px",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                />
            </Card>

            {/* ---------- Generate Salary Modal ---------- */}
            <Modal
                open={isGenerateModalVisible}
                onCancel={handleCloseGenerateModal}
                title="Generate Salary"
                footer={null}
                width={500}
            >
                <Form layout="vertical" form={generateForm}>
                    <Form.Item
                        label="Select Month and Year"
                        name="month_year"
                        rules={[{ required: true, message: "Please select month and year!" }]}
                    >
                        <DatePicker picker="month" style={{ width: "100%" }} format="MMMM YYYY" placeholder="Select Month and Year" />
                    </Form.Item>
                    <div style={{ textAlign: "right" }}>
                        <Button onClick={handleCloseGenerateModal} style={{ marginRight: 10 }}>Cancel</Button>
                        <Button
                            type="primary" loading={generating} onClick={handleGenerateSalary}
                            style={{ backgroundColor: "#0e497b", borderColor: "#0e497b", fontWeight: "500" }}
                        >
                            Generate
                        </Button>
                    </div>
                </Form>
            </Modal>

            {/* ---------- Upload Modal ---------- */}
            <Modal
                open={isUploadModalVisible}
                onCancel={handleCloseUploadModal}
                title="Upload Salary Details"
                footer={null}
                width={500}
            >
                <Form layout="vertical" form={uploadForm}>
                    <Form.Item
                        label="Select Month and Year"
                        name="month_year"
                        rules={[{ required: true, message: "Please select month and year!" }]}
                    >
                        <DatePicker picker="month" style={{ width: "100%" }} format="MMMM YYYY" placeholder="Select Month and Year" />
                    </Form.Item>
                    <Form.Item
                        label="Upload File"
                        name="file"
                        valuePropName="fileList"
                        getValueFromEvent={(e) => (Array.isArray(e) ? e : e && e.fileList)}
                        rules={[{ required: true, message: "Please upload a file!" }]}
                    >
                        <Upload beforeUpload={() => false} accept=".xlsx" maxCount={1}>
                            <Button icon={<UploadOutlined />}>Choose File</Button>
                        </Upload>
                    </Form.Item>
                    <div style={{ textAlign: "right" }}>
                        <Button onClick={handleCloseUploadModal} style={{ marginRight: 10 }}>Cancel</Button>
                        <Button
                            type="primary" loading={uploading} onClick={handleUploadSalary}
                            style={{ backgroundColor: "#0e497b", borderColor: "#0e497b", fontWeight: "500" }}
                        >
                            Upload
                        </Button>
                    </div>
                </Form>
            </Modal>

            {/* ---------- Edit Modal ---------- */}
            <Modal
                open={isModalVisible}
                onCancel={handleCloseModal}
                footer={null}
                title={`Edit Salary – ${selectedRecord?.employee?.emp_code || ""} – ${selectedRecord?.employee?.first_name || ""}`}
                width={900}
            >
                <SalaryCalculation salary_details={selectedSalary} />
            </Modal>

            {/* ---------- Superuser Reject Modal ---------- */}
            <Modal
                open={isRejectModalVisible}
                onCancel={() => { setIsRejectModalVisible(false); setRejectReason(""); }}
                title={`Reject ${selectedRowKeys.length} Salary Record(s)`}
                footer={[
                    <Button key="cancel" onClick={() => { setIsRejectModalVisible(false); setRejectReason(""); }}>
                        Cancel
                    </Button>,
                    <Button
                        key="reject" danger type="primary" loading={rejecting}
                        disabled={!rejectReason.trim()} onClick={handleSuperuserReject}
                    >
                        Confirm Reject
                    </Button>,
                ]}
                width={480}
            >
                <p style={{ marginBottom: 8, fontWeight: 500 }}>
                    Rejection Reason <span style={{ color: "red" }}>*</span>
                </p>
                <Input.TextArea
                    rows={4}
                    placeholder="Enter reason for rejection..."
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                />
            </Modal>
        </>
    );
}